import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Play, Info, Trash2 } from 'lucide-react';
import './continue-menu.css';

export interface NyomiriContinueMenuProps {
  open: boolean;
  anchorPosition?: { left: number; top: number } | null;
  anchorEl?: HTMLElement | null;
  onClose: () => void;
  onContinue: () => void;
  onOpenDetail: () => void;
  onRemove: () => void;
  title: string;
}

export function NyomiriContinueMenu({
  open,
  anchorPosition,
  anchorEl,
  onClose,
  onContinue,
  onOpenDetail,
  onRemove,
  title,
}: NyomiriContinueMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open) return;

    let targetLeft = 0;
    let targetTop = 0;

    if (anchorPosition) {
      targetLeft = anchorPosition.left;
      targetTop = anchorPosition.top;
    } else if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      targetLeft = rect.left;
      targetTop = rect.bottom + 6;
    }

    const menuEl = menuRef.current;
    if (menuEl) {
      const menuWidth = menuEl.offsetWidth || 220;
      const menuHeight = menuEl.offsetHeight || 160;
      const padding = 12;

      // Ensure menu stays within viewport
      if (targetLeft + menuWidth > window.innerWidth - padding) {
        targetLeft = Math.max(padding, window.innerWidth - menuWidth - padding);
      }
      if (targetTop + menuHeight > window.innerHeight - padding) {
        // Position upwards if overflowing bottom
        if (anchorEl) {
          const rect = anchorEl.getBoundingClientRect();
          targetTop = Math.max(padding, rect.top - menuHeight - 6);
        } else {
          targetTop = Math.max(padding, window.innerHeight - menuHeight - padding);
        }
      }
    }

    setCoords({
      left: Math.max(8, targetLeft),
      top: Math.max(8, targetTop),
    });
  }, [open, anchorPosition, anchorEl]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Back' || e.keyCode === 10009 || e.keyCode === 461) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }

      // Keyboard arrow navigation inside menu
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const items = menuRef.current?.querySelectorAll<HTMLButtonElement>('.yomiri-context-menu-item');
        if (!items || items.length === 0) return;

        const currentActive = document.activeElement;
        const currentIndex = Array.from(items).indexOf(currentActive as HTMLButtonElement);

        let nextIndex = 0;
        if (e.key === 'ArrowDown') {
          nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        }

        items[nextIndex]?.focus();
      }
    };

    const handlePointerDownOutside = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('pointerdown', handlePointerDownOutside, true);
    window.addEventListener('scroll', onClose, { passive: true, capture: true });

    // Focus first action initially
    const timer = setTimeout(() => {
      menuRef.current?.querySelector<HTMLButtonElement>('.yomiri-context-menu-item')?.focus();
    }, 20);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('pointerdown', handlePointerDownOutside, true);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="yomiri-context-menu"
      style={{
        top: coords.top,
        left: coords.left,
      }}
      role="menu"
      tabIndex={-1}
      aria-label={`Acciones de continuar para ${title}`}
    >
      <div className="yomiri-context-menu-header" title={title}>
        {title}
      </div>

      <button
        type="button"
        className="yomiri-context-menu-item is-primary"
        role="menuitem"
        onClick={() => {
          onClose();
          onContinue();
        }}
      >
        <span className="yomiri-context-menu-item-icon">
          <Play size={16} fill="currentColor" />
        </span>
        <span>Continuar</span>
      </button>

      <button
        type="button"
        className="yomiri-context-menu-item"
        role="menuitem"
        onClick={() => {
          onClose();
          onOpenDetail();
        }}
      >
        <span className="yomiri-context-menu-item-icon">
          <Info size={16} />
        </span>
        <span>Abrir ficha</span>
      </button>

      <div className="yomiri-context-menu-divider" role="separator" />

      <button
        type="button"
        className="yomiri-context-menu-item is-destructive"
        role="menuitem"
        onClick={() => {
          onClose();
          onRemove();
        }}
      >
        <span className="yomiri-context-menu-item-icon">
          <Trash2 size={16} />
        </span>
        <span>Quitar de Continuar</span>
      </button>
    </div>,
    document.body
  );
}
